<script setup lang="ts">
import { computed, ref } from 'vue';
import { match, P } from 'ts-pattern';
import { StudentCourseStatus, StudentExamStatus } from '@/types/FlightSchoolStudent';
import { FlightSchoolStatus } from '@/types/FlightSchoolStatus';
import { toTitleCase } from '@/functions/toTitleCase';

const props = defineProps<{
    status: StudentCourseStatus | FlightSchoolStatus | StudentExamStatus,
    tooltip?: string,
}>();

const showTooltip = ref(false);

/**
 * If a tooltip is not provided, derive a tooltip from the status.
 */
const statusTooltip = computed(() => props.tooltip
    ?? match(props.status)
        .with(StudentCourseStatus.dropped, () => 'Inactive and has not completed the course.')
        .with(StudentCourseStatus.locked, () => 'Locked out manually by an administrator.')
        .with(FlightSchoolStatus.Active, () => 'Actively accepting student enrollments.')
        .with(FlightSchoolStatus.NotVisible, () => 'Actively accepting Gleim tester enrollments.')
        .with(FlightSchoolStatus.Archived, () => 'No longer accepting student enrollments.')
        .with(StudentCourseStatus.enrolled, () => 'Active course access.')
        .with(StudentCourseStatus.graduated, () => 'Course completed.')
        .with(StudentExamStatus.passed, () => 'Exam passed and score submitted.')
        .with(StudentExamStatus.failed, () => 'Exam failed and score submitted.')
        .with(StudentExamStatus.nottaken, () => 'No score submitted.')
        .otherwise(() => 'unknown'));

const statusBadgeColor = computed(() => match(props.status)
    .with(P.union(StudentCourseStatus.dropped, StudentExamStatus.failed), () => 'badge-red')
    .with(StudentCourseStatus.locked, () => 'badge-med-light-gray')
    .with(FlightSchoolStatus.NotVisible, () => 'badge-blue')
    .with(
        P.union(StudentExamStatus.nottaken, FlightSchoolStatus.Archived),
        () => 'badge-gray',
    )
    .otherwise(() => 'badge-green'));

const statusBadgeOutlined = computed(() => match(props.status)
    .with(P.union(
        StudentCourseStatus.enrolled,
        StudentCourseStatus.dropped,
        StudentExamStatus.nottaken,
    ), () => true)
    .otherwise(() => false));

const ariaLabel = computed(() => `${props.status} - ${statusTooltip.value}`);
</script>
<template>
    <q-badge
        class="q-pa-sm rounded-borders"
        :color="statusBadgeColor"
        :text-color="statusBadgeColor"
        :outline="statusBadgeOutlined"
        :label="toTitleCase(status)"
        :aria-label="ariaLabel"
        tabindex="0"
        @focus="showTooltip = true"
        @blur="showTooltip = false"
    >
        <q-icon
            v-if="status === StudentCourseStatus.locked"
            class="q-pl-xs"
            color="badge-white"
            name="o_lock"
        />
        <q-tooltip
            v-if="statusTooltip"
            v-model="showTooltip"
            anchor="top middle"
            self="bottom middle"
            class="fsum-tooltip arrow-bottom"
        >
            {{ statusTooltip }}
        </q-tooltip>
    </q-badge>
</template>
<style lang="scss">
</style>
